/* @refresh reload */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translations } from '../translations';
import { LanguageContext, type Language } from './useLanguage'

export const LanguageProvider: React.FC<{ children: React.ReactNode; initialLanguage?: Language }> = ({
  children,
  initialLanguage,
}) => {
  const [language, setLanguage] = useState<Language>(initialLanguage ?? 'zh');

  useEffect(() => {
    if (initialLanguage) {
      setLanguage(initialLanguage);
    }
  }, [initialLanguage]);

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.');

    const getValue = (lang: Language) => {
      let current: any = translations[lang];
      for (const k of keys) {
        if (current && typeof current === 'object' && k in current) {
          current = current[k as keyof typeof current];
        } else {
          return undefined;
        }
      }
      return current;
    };

    const value = getValue(language);

    const applyParams = (text: string) => {
      if (!params) {
        return text;
      }
      return text.replace(/\{(\w+)\}/g, (match, token) => {
        if (Object.prototype.hasOwnProperty.call(params, token)) {
          return String(params[token]);
        }
        return match;
      });
    };

    // Return translation if found and is a string
    if (typeof value === 'string') {
      return applyParams(value);
    }

    // Fallback to English
    const enValue = getValue('en');
    if (typeof enValue === 'string') {
      return applyParams(enValue);
    }

    // Fallback to key if nothing found
    return key;
  }, [language]);

  const contextValue = useMemo(
    () => ({ language, setLanguage, t }),
    [language, t],
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export { useLanguage } from './useLanguage'
